import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import LevelEditor from '../../components/LevelEditor';
import GamePlayer, { cleanGame, gameThumb, type Game } from '../../components/GamePlayer';
import RaceEditor from '../../components/RaceEditor';
import Loading from '../../components/Loading';
import type { Level } from '../../game/levels';
import type { RaceLevel } from '../../game/race';
import { sfxCoin } from '../../game/sfx';
import { fetchShop, finishGame, makeGame, playGame, sandDollars, type ArcadeGame, type ShopData } from '../../lib/shop';
import { TOWER_COST } from '../../lib/tower';
import { useClassroom } from '../../state/Classroom';
import ClassGamePlay, { useQuizState } from './ClassGamePlay';
import SnakeGame from './SnakeGame';
import Fortress from './Tower';

// ─────────────────────────────────────────────────────────────────────────────
// Ocean (/ocean): every game is here, and students pick one. The Shop is only
// the shop (routes/student/Shop.tsx).
//   /ocean               the picker
//   /ocean/explore       the class ocean (routes/student/Ocean.tsx)
//   /ocean/arcade        play the levels and race courses classmates made
//   /ocean/fortress      the Sunken Fortress, a new dungeon every week (./Tower.tsx)
//   /ocean/make          build a platformer level or a race course
//   /ocean/class-game    the live class game, when the teacher opens one
//   /ocean/snake         Sea Snake, with your flashcard words
// ─────────────────────────────────────────────────────────────────────────────

type GameId = 'explore' | 'arcade' | 'fortress' | 'make' | 'class-game' | 'snake';
const GAMES: { id: GameId; icon: string; name: string; blurb: string }[] = [
  { id: 'explore', icon: '🌊', name: 'Explore the ocean', blurb: 'Swim, fish, mine and build in your class’s ocean.' },
  { id: 'fortress', icon: '🏰', name: 'Sunken Fortress', blurb: 'A new 3D dungeon every week. Push verb blocks into their pronouns, fight, and beat the Kraken.' },
  { id: 'arcade', icon: '🕹️', name: 'Arcade', blurb: 'Play the levels and race courses your classmates made.' },
  { id: 'make', icon: '🛠️', name: 'Make a game', blurb: 'Build a platformer level or a race course for the arcade.' },
  { id: 'snake', icon: '🐍', name: 'Sea Snake', blurb: 'Eat the right translations of your flashcard words.' },
  { id: 'class-game', icon: '🎮', name: 'Class game', blurb: 'Your teacher’s live quiz game, when it’s open.' },
];

/** The shop's data (balance, your fish, the arcade's games), for the games that need it. */
function useShopData() {
  const { active } = useClassroom();
  const [data, setData] = useState<ShopData | null>(null);
  const [error, setError] = useState('');
  const load = useCallback(async () => {
    if (!active) return;
    try { setData(await fetchShop(active.id)); setError(''); }
    catch (e) { setError(e instanceof Error ? e.message : 'Could not load the games.'); }
  }, [active]);
  useEffect(() => { void load(); }, [load]);
  const setBalance = (balance: number) => setData(d => (d ? { ...d, balance } : d));
  return { data, error, load, setBalance };
}

/** /ocean: pick a game. */
export function OceanGames() {
  const { active } = useClassroom();
  const quiz = useQuizState();
  const live = !!quiz && quiz.phase !== 'closed';
  if (!active) return <div className="notice">Join a class to play.</div>;
  return (
    <div className="shop ocean-games">
      <h1>🌊 Ocean</h1>
      <p className="muted" style={{ marginTop: -6 }}>Pick a game.</p>
      <div className="game-pick">
        {GAMES.map(g => (
          <Link key={g.id} to={`/ocean/${g.id}`} className={`game-tile panel${g.id === 'class-game' && live ? ' live' : ''}`}>
            <span className="game-tile-icon" aria-hidden>{g.icon}</span>
            <strong>{g.name}</strong>
            <span className="small muted">{g.blurb}</span>
            {g.id === 'fortress' && <span className="small game-tile-tag">🪙 {TOWER_COST} a try</span>}
            {g.id === 'class-game' && live && <span className="small game-tile-tag">🔴 Open now!</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

const Back = () => <Link to="/ocean" className="small game-back">← All games</Link>;

/** /ocean/:game (all but explore, which is the ocean itself). */
export function OceanGame() {
  const { game } = useParams();
  const { active } = useClassroom();
  const navigate = useNavigate();
  const shop = useShopData();
  const needsShop = game === 'arcade' || game === 'fortress' || game === 'make';
  if (!GAMES.some(g => g.id === game) || game === 'explore') return <Navigate to="/ocean" replace />;
  if (!active) return <div className="notice">Join a class to play.</div>;
  if (game === 'class-game') return <div><Back /><ClassGamePlay /></div>;
  if (game === 'snake') return <SnakeGame onExit={() => navigate('/ocean')} back="All games" />;
  if (needsShop && shop.error && !shop.data) return <div className="notice bad">{shop.error}</div>;
  if (!shop.data) return <Loading what="the game" />;
  const data = shop.data, balance = data.balance ?? 0;
  const head = (title: string) => (
    <div className="shop-head">
      <div><Back /><h1>{title}</h1></div>
      <span className="shop-balance" aria-label={`${sandDollars(balance)} sand dollars`}>🪙 {sandDollars(balance)}</span>
    </div>
  );
  return (
    <div className="shop">
      {game === 'arcade' && <>{head('🕹️ Arcade')}<Arcade data={data} reload={shop.load} setBalance={shop.setBalance} goMake={() => navigate('/ocean/make')} /></>}
      {game === 'fortress' && <>{head('🏰 Sunken Fortress')}<Fortress cid={active.id} language={active.language} look={data.look} balance={balance} setBalance={shop.setBalance} /></>}
      {game === 'make' && <>{head('🛠️ Make a game')}<Make data={data} balance={balance} setBalance={shop.setBalance} reload={shop.load} /></>}
    </div>
  );
}

// ── Make a game ─────────────────────────────────────────────────────────────
function Make({ data, balance, setBalance, reload }: { data: ShopData; balance: number; setBalance: (b: number) => void; reload: () => Promise<void> }) {
  const { active } = useClassroom();
  const navigate = useNavigate();
  const [maker, setMakerState] = useState<'level' | 'race'>(() => { try { return localStorage.getItem('maker-mode') === 'race' ? 'race' : 'level'; } catch { return 'level'; } });
  const setMaker = (m: 'level' | 'race') => { setMakerState(m); try { localStorage.setItem('maker-mode', m); } catch { /* fine */ } };
  const publish = async (title: string, level: Level | RaceLevel) => {
    if (!active) return;
    const r = await makeGame(active.id, title, level);
    sfxCoin(); setBalance(r.balance); await reload(); navigate('/ocean/arcade');
  };
  return (
    <>
      <div className="seg maker-mode" role="radiogroup" aria-label="Kind of game">
        <button type="button" role="radio" aria-checked={maker === 'level'} className={maker === 'level' ? 'on' : ''} onClick={() => setMaker('level')}>🕹️ Platformer</button>
        <button type="button" role="radio" aria-checked={maker === 'race'} className={maker === 'race' ? 'on' : ''} onClick={() => setMaker('race')}>🏎️ Race</button>
      </div>
      {maker === 'level' ? (
        <>
          <p className="muted">Build a level for your classmates: paint the ground, add spikes, springs, coins, creatures and bosses, and put a flag at the end. Once your teacher says yes, it goes in the <strong>Arcade</strong> for your classmates to play.</p>
          <LevelEditor balance={balance} look={data.look} onPublish={publish} />
        </>
      ) : (
        <>
          <p className="muted">Build a race course: shape the hills, ramps and pits, add coins, boosts and checkpoints, and put the finish line at the end. Classmates drive it in a race car, like Hill Climb Racing.</p>
          <RaceEditor balance={balance} look={data.look} onPublish={publish} />
        </>
      )}
    </>
  );
}

// ── Arcade ──────────────────────────────────────────────────────────────────
const STATUS: Record<ArcadeGame['status'], string> = { pending: '⏳ Waiting for your teacher', approved: '✅ In the arcade', rejected: '✋ Not approved', removed: '🚫 Taken down' };

function Arcade({ data, reload, setBalance, goMake }: { data: ShopData; reload: () => Promise<void>; setBalance: (b: number) => void; goMake: () => void }) {
  const [playing, setPlaying] = useState<{ play: string; title: string; level: Game; paid: number } | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const balance = data.balance ?? 0;
  const levels = data.games.filter(g => g.kind === 'level');
  const theirs = levels.filter(g => !g.mine && g.status === 'approved');
  const mine = levels.filter(g => g.mine);

  const start = async (g: ArcadeGame) => {
    setBusy(g.id); setError('');
    try {
      const r = await playGame(g.id);
      setBalance(r.balance); if (!g.mine) sfxCoin();
      const level = cleanGame(r.content);
      if (!level) throw new Error('This game is broken.');
      setPlaying({ play: r.play, title: r.title, level, paid: g.mine ? 0 : g.price });
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not start the game.'); }
    setBusy('');
  };

  if (playing) return (
    <GamePlayer game={playing.level} look={data.look} title={playing.title}
      onDone={res => { void finishGame(playing.play, res.won).catch(() => {}); }}
      onClose={() => { setPlaying(null); void reload(); }} />
  );

  return (
    <div className="arcade">
      {error && <div className="notice bad" role="alert">{error}</div>}
      {theirs.length === 0 ? (
        <div className="notice">The arcade is empty so far. <button type="button" className="linkish" onClick={goMake}>Make the first game!</button></div>
      ) : (
        <div className="arcade-grid">
          {theirs.map(g => (
            <div key={g.id} className="panel arcade-card">
              <div className="arcade-kind" aria-hidden>{g.mode === 'race' ? '🏎️' : '🕹️'}</div>
              <h3 dir="auto">{g.title}</h3>
              <div className="small muted">
                {g.author ? `by ${g.author} · ` : ''}{g.plays} play{g.plays === 1 ? '' : 's'} · beaten by {g.clears}
              </div>
              {g.best != null && <div className="small">{g.best > 0 ? '🏁 You beat it!' : 'Not beaten yet'}</div>}
              <button type="button" className="primary" disabled={!!busy || balance < g.price} onClick={() => void start(g)}>
                {busy === g.id ? '…' : `Play · 🪙 ${g.price}`}
              </button>
            </div>
          ))}
        </div>
      )}

      {mine.length > 0 && (
        <div className="panel" style={{ marginTop: 20 }}>
          <div className="panel-head"><h3>My games</h3><button type="button" className="small" onClick={goMake}>+ Make another</button></div>
          {mine.map(g => (
            <div key={g.id} className="my-game">
              <MyThumb content={g.content} />
              <div className="grow">
                <strong dir="auto">{g.title}</strong>
                <div className="small muted">{STATUS[g.status]} · {g.status === 'pending' ? 'your teacher sets the price' : `🪙 ${g.price} to play`} · {g.plays} play{g.plays === 1 ? '' : 's'} · beaten by {g.clears}</div>
                {g.note && <div className="small">Your teacher: “{g.note}”</div>}
              </div>
              <button type="button" disabled={!!busy} onClick={() => void start(g)}>Play (free)</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function MyThumb({ content }: { content: unknown }) {
  const src = useMemo(() => { const l = cleanGame(content); return l ? gameThumb(l, 160) : ''; }, [content]);
  return src ? <img className="level-thumb" src={src} alt="" /> : null;
}
